// Нижняя полоса: счёт игрока, ставка с кнопками +/- и выигрыш последнего раунда.
//
// GDD, раздел 3.1, называет из этого одни настройки ставки и рисует их иконкой. Сами поля
// BALANCE, STAKE и LAST WIN описаны только ассетами, см. ADR-34.
//
// Полоса стоит на экране, а не в мире игры: взгляд её не двигает, как не двигает и кнопку
// спина. Тень тянется на всю видимую ширину, поэтому полоса участвует в layout, а всё,
// что на ней стоит, ставится в координатах макета: под интерфейс там отведён низ.
//
// Кнопки ставки гаснут на краях лестницы и на всё время раунда: ставку игрок меняет между
// раундами, а платит ту, которой начал, см. game/spinRound.ts.
//
// Своих денег полоса не считает. Все суммы она берёт у кошелька, а тот — у бэкенда,
// см. ADR-09 и game/wallet.ts.

import { Container, Sprite, Text, type TextStyleOptions, type Texture } from 'pixi.js';

import type { Cents } from '../api/gameConfig';
import { MISC_UI_SHEET, TITLE_FONT, type MiscUiFrame } from '../assets/catalog';
import type { AssetManager, TextureSheet } from '../assets/manager';
import type { Wallet } from '../game/wallet';
import { text, textFamily } from '../i18n/text';

import { RoundButton } from './button';
import { fitLabel } from './label';
import { moneyText } from './amount';
import type { GameLayout } from './layout';
import { DESIGN_HEIGHT } from './scene';

/** Середина полосы по высоте в координатах макета. Под интерфейсом отведён низ экрана. */
const BAR_Y: number = DESIGN_HEIGHT / 2 - 110;

/**
 * Верх тени над серединой полосы.
 *
 * Он поднят над подписями полей: край тени идёт прозрачностью, и подпись на нём читалась бы
 * половиной букв. Низ тени нарисован в упор, поэтому тень тянется до низа экрана, см. layout.
 */
const SHADOW_TOP: number = BAR_Y - 80;

/**
 * Середина каждого поля по горизонтали.
 *
 * Ставка стоит по центру макета, под серединой машины. Счёт и выигрыш отходят от неё
 * на одно расстояние, поэтому поля стоят вразрядку. Справа остаётся место автоигре и спину.
 */
const FIELD_X: number = 350;

const BALANCE_X: number = -FIELD_X;
const STAKE_X: number = 0;
const LAST_WIN_X: number = FIELD_X;

/** На узком экране плашки сходятся к центру, сохраняя читаемый масштаб. */
const COMPACT_FIELD_X: number = 260;

/** Насколько кнопка ставки отходит от середины плашки. Наведённый кадр шире, и он тоже не задевает её. */
const STAKE_BUTTON_X: number = 140;

/** Насколько подпись поднята над серединой своей плашки. */
const LABEL_Y: number = -46;

/** Насколько сообщение о пустом счёте опущено под полосу. */
const WARNING_Y: number = 62;

/** Размер подписи поля и размер суммы под ней. */
const LABEL_SIZE: number = 22;
const VALUE_SIZE: number = 30;

/** Подпись поля: мелкие светлые буквы вразрядку. */
function labelStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: LABEL_SIZE,
    fontWeight: 'bold',
    fill: 0xd8c8ac,
    letterSpacing: 3,
    align: 'center',
  };
}

/** Сумма под подписью: она крупнее и светлее, на неё игрок и смотрит. */
function valueStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: VALUE_SIZE,
    fontWeight: 'bold',
    fill: 0xfff4dd,
    align: 'center',
  };
}

/** Сообщение о пустом счёте: тем же шрифтом, но тревожного цвета. */
function warningStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: LABEL_SIZE,
    fontWeight: 'bold',
    fill: 0xe4784a,
    letterSpacing: 3,
    align: 'center',
  };
}

/**
 * Поле полосы: плашка, подпись над ней и сумма на ней.
 *
 * Ширину полю задаёт плашка, и за неё поле не выходит: «LAST WIN» по-латышски втрое длиннее
 * английского, а поля стоят через 460 единиц макета, и рядом со ставкой ещё две кнопки.
 * Поэтому подпись ужимается под свою плашку, см. ui/label.ts.
 */
class BarField {
  readonly view: Container = new Container();

  readonly #caption: Text;
  readonly #value: Text;
  readonly #width: number;

  constructor(plate: Texture, label: string, family: string, x: number) {
    const back: Sprite = new Sprite(plate);
    back.anchor.set(0.5);

    this.#width = plate.width;

    this.#caption = new Text({ text: label, style: labelStyle(family) });
    this.#caption.anchor.set(0.5);
    this.#caption.position.set(0, LABEL_Y);
    fitLabel(this.#caption, { width: this.#width });

    this.#value = new Text({ text: '', style: valueStyle(family) });
    this.#value.anchor.set(0.5);

    this.view.position.set(x, BAR_Y);
    this.view.addChild(back, this.#caption, this.#value);
  }

  /** Пишет сумму на плашке. Сумма растёт со ставкой, поэтому она тоже меряется по плашке. */
  set amount(cents: Cents) {
    this.#value.text = moneyText(cents);
    fitLabel(this.#value, { width: this.#width });
  }

  set label(value: string) {
    this.#caption.text = value;
    fitLabel(this.#caption, { width: this.#width });
  }
}

/** Нижняя полоса игры. */
export class BottomBar {
  readonly view: Container = new Container();

  readonly #wallet: Wallet;
  readonly #shadow: Sprite;

  readonly #balance: BarField;
  readonly #stake: BarField;
  readonly #lastWin: BarField;

  readonly #up: RoundButton;
  readonly #down: RoundButton;

  readonly #warning: Text;

  /** Может ли игрок двигать ставку. Ложь — раунд идёт, и кнопки погашены. */
  #open = true;

  constructor(assets: AssetManager, wallet: Wallet) {
    const ui: TextureSheet<MiscUiFrame> = assets.sheet(MISC_UI_SHEET);
    const family: string = assets.font(TITLE_FONT);

    this.#wallet = wallet;

    // Тень тянется во всю ширину экрана и до его низа, поэтому её размер ставит layout,
    // а не эта строка. Верх у неё свой и на месте, поэтому якорь стоит на нём.
    this.#shadow = new Sprite(ui.barShadow);
    this.#shadow.anchor.set(0.5, 0);
    this.#shadow.position.set(0, SHADOW_TOP);

    this.#balance = new BarField(ui.barBalance, text('balance'), family, BALANCE_X);
    this.#stake = new BarField(ui.barStake, text('stake'), family, STAKE_X);
    this.#lastWin = new BarField(ui.barLastWin, text('lastWin'), family, LAST_WIN_X);

    this.#down = new RoundButton({ idle: ui.stakeDownIdle, hover: ui.stakeDownHover, active: ui.stakeDownActive }, () => {
      wallet.lower();
    });

    this.#up = new RoundButton({ idle: ui.stakeUpIdle, hover: ui.stakeUpHover, active: ui.stakeUpActive }, () => {
      wallet.raise();
    });

    this.#down.view.position.set(STAKE_X - STAKE_BUTTON_X, BAR_Y);
    this.#up.view.position.set(STAKE_X + STAKE_BUTTON_X, BAR_Y);

    this.#warning = new Text({ text: text('noFunds'), style: warningStyle(family) });
    this.#warning.anchor.set(0.5);
    this.#warning.position.set(STAKE_X, BAR_Y + WARNING_Y);
    this.#warning.visible = false;

    this.view.addChild(this.#shadow, this.#balance.view, this.#stake.view, this.#lastWin.view, this.#down.view, this.#up.view, this.#warning);

    wallet.listen(() => this.#draw());
    this.#draw();
  }

  /** Двигает затухание кнопок ставки. */
  update(seconds: number): void {
    this.#up.update(seconds);
    this.#down.update(seconds);
  }

  /**
   * Тянет тень полосы на всю видимую область: за краями макета экран не должен светиться.
   *
   * Вниз тень идёт до края экрана, а не на свою высоту: низ у неё нарисован в упор, и полоса
   * фона под ней читалась бы как щель.
   */
  layout(layout: GameLayout): void {
    const { bottom, composition, viewport } = layout;

    this.view.scale.set(bottom.scale);
    this.view.position.set(0, bottom.centerY - BAR_Y * bottom.scale);

    if (composition === 'mobile') {
      this.#balance.view.x = -COMPACT_FIELD_X;
      this.#stake.view.x = 0;
      this.#lastWin.view.x = COMPACT_FIELD_X;
      this.#down.view.x = STAKE_X - STAKE_BUTTON_X;
      this.#up.view.x = STAKE_X + STAKE_BUTTON_X;
    } else {
      this.#balance.view.x = BALANCE_X;
      this.#stake.view.x = STAKE_X;
      this.#lastWin.view.x = LAST_WIN_X;
      this.#down.view.x = STAKE_X - STAKE_BUTTON_X;
      this.#up.view.x = STAKE_X + STAKE_BUTTON_X;
    }

    this.#shadow.width = viewport.width / bottom.scale;
    this.#shadow.height = (viewport.height / 2 - this.view.y) / bottom.scale - SHADOW_TOP;
  }

  /**
   * Открывает или закрывает выбор ставки.
   *
   * Раунд её уже оплатил, поэтому менять её на ходу игроку нечем: следующий раунд начнётся
   * с той, что он выберет после этого.
   */
  set open(value: boolean) {
    this.#open = value;
    this.#draw();
  }

  /** Переписывает только локализованные подписи, не трогая суммы и кнопки. */
  refreshText(): void {
    this.#balance.label = text('balance');
    this.#stake.label = text('stake');
    this.#lastWin.label = text('lastWin');
    this.#warning.text = text('noFunds');
  }

  /** Переписывает полосу по кошельку. */
  #draw(): void {
    this.#balance.amount = this.#wallet.balance;
    this.#stake.amount = this.#wallet.stake;
    this.#lastWin.amount = this.#wallet.lastWin;

    this.#up.enabled = this.#open && this.#wallet.canRaise;
    this.#down.enabled = this.#open && this.#wallet.canLower;

    // На ходу раунда счёт стоит списанным и легко оказывается меньше ставки. Сообщать об этом
    // посреди прокрутки незачем: игрок уже крутит, и решать ему будет нечего до конца раунда.
    this.#warning.visible = this.#open && !this.#wallet.affords;
  }
}
